import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaUser, FaChevronDown, FaUserCircle, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useUsers from "./useHook";
import "./style.css";
import { clearStoredUser, filterRoutesByPermissions, getStoredUser } from "../../Utils/permissionAccess";
import routes from "../../Utils/routes";
import { useLanguage } from "../../Utils/LanguageContext";

function Navbar({ onToggleSidebar, isMobile }) {
  const { getAdminDetails } = useUsers();
  const { language, toggleLanguage } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);

  const allowedRoutes = useMemo(() => {
    return filterRoutesByPermissions(routes, userData || getStoredUser());
  }, [userData]);

  const searchableRoutes = useMemo(() => {
    const flattenRoutes = (items, parentLabel = "") => {
      let flat = [];
      items.forEach((item) => {
        if (item.visible === false) return;
        const currentLabel = parentLabel ? `${parentLabel} > ${item.label}` : item.label;
        if (item.isGroup && item.children) {
          flat = [...flat, ...flattenRoutes(item.children, currentLabel)];
        } else if (item.path && item.label) {
          flat.push({ ...item, searchLabel: currentLabel });
        }
      });
      return flat;
    };
    return flattenRoutes(allowedRoutes);
  }, [allowedRoutes]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const lowerQuery = searchQuery.toLowerCase();
    return searchableRoutes.filter(
      (route) => route.searchLabel.toLowerCase().includes(lowerQuery)
    );
  }, [searchQuery, searchableRoutes]);

  useEffect(() => {
    getAdminDetails(setUserData);
  }, []);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handlePageView = () => {
    setIsDropdownOpen(false);
    navigate("/cms");
  };

  const handleProfileView = () => {
    setIsDropdownOpen(false);
    navigate("/profile");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    clearStoredUser();
    setIsDropdownOpen(false);
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      setIsDropdownOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const userRoles = useMemo(() => {
    const formatRole = (roleName) => {
      if (!roleName) return "";
      return roleName
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    if (userData?.assignedRoles && Array.isArray(userData.assignedRoles)) {
      return userData.assignedRoles.map((role) => formatRole(role.name)).join(", ");
    }
    return formatRole(userData?.role) || "Admin";
  }, [userData]);

  return (
    <div
      id="navbar"
      className="flex justify-between items-center brand-gradient text-white h-16 px-4 p-0"
    >
      <div className="flex items-center gap-4">
        {isMobile && (
          <button
            onClick={onToggleSidebar}
            data-sidebar-toggle
            className="p-2 rounded-lg hover:bg-white/10 transition-colors lg:hidden"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        )}
        <h1 className="text-2xl font-bold hidden sm:block whitespace-nowrap">
          Hello {userData?.firstName || "Admin"}
        </h1>
      </div>

      {/* Global Search Bar */}
      <div className="flex-1 max-w-xl mx-4" ref={searchRef}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search menus (e.g., House Design)..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsSearchOpen(true);
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsSearchOpen(true);
            }}
            className="block w-full pl-10 pr-3 py-2 border border-white/20 rounded-lg leading-5 bg-white/10 text-white placeholder-gray-200 focus:outline-none focus:bg-white focus:text-gray-900 focus:placeholder-gray-500 sm:text-sm transition-colors duration-200"
          />

          {isSearchOpen && searchQuery && (
            <div
              className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {searchResults.length > 0 ? (
                <ul className="py-1">
                  {searchResults.map((route, index) => (
                    <li key={index}>
                      <button
                        onClick={() => {
                          navigate(route.path);
                          setIsSearchOpen(false);
                          setSearchQuery("");
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-purple-700"
                      >
                        <div className="font-medium text-gray-900">{route.label}</div>
                        <div className="text-xs text-gray-500">{route.searchLabel}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No results found for "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Language Toggle */}
      <div className="relative flex items-center gap-6 me-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              {userData?.profileImage ? (
                <img
                  src={userData.profileImage}
                  alt="User Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <FaUserCircle className="text-gray-700" />
              )}
            </div>

            <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
          </div>

          <div>
            <h2 className="text-lg font-medium text-black">
              {userData?.firstName || ""} {userData?.lastName || ""}
            </h2>
            <p className="text-sm text-gray-900 capitalize">{userRoles}</p>
          </div>
        </div>

        <FaChevronDown
          className={`text-black transform cursor-pointer ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
          onClick={toggleMenu}
        />

        {isDropdownOpen && (
          <div
            className="absolute z-10 right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg"
            style={{ top: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <ul>
              {/* <li>
                <button
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                  onClick={handlePageView}
                >
                  Setting
                </button>
              </li> */}
              <li>
                <button
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                  onClick={handleProfileView}
                >
                  Profile
                </button>
              </li>
              <li>
                <button
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default Navbar;
